import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../App.jsx';
import { Mail, Lock, User, PhoneCall, ArrowLeft, Eye, EyeOff, Search, Briefcase, ChevronRight, RefreshCw } from 'lucide-react';
import { COLORS } from '../config/constants';

const SignUpScreen = () => {
    const { user, signUp, addRoleToUser, fullUserData } = useAppContext();
    const navigate = useNavigate();

    const isAddingRole = !!user;

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [role, setRole] = useState('client');
    const [roleSelected, setRoleSelected] = useState(isAddingRole);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isAddingRole && fullUserData) {
            const currentRoles = Object.keys(fullUserData.roles || {}).filter(r => fullUserData.roles[r]);
            const profile = fullUserData[`profile_${currentRoles[0]}`];
            setName(profile?.name || '');
            setEmail(user.email || '');
            setPhone(profile?.phone || '');

            // Se já tem um papel, sugere o outro
            const nextRole = currentRoles.includes('client') ? 'servi' : 'client';
            setRole(nextRole);
            setRoleSelected(true);
        }
    }, [isAddingRole, fullUserData, user]);

    const handleSignUp = async (e) => {
        e.preventDefault();
        setError("");
        if (!isAddingRole && password !== confirmPassword) {
            setError("As senhas não coincidem.");
            return;
        }
        if (!termsAccepted) {
            setError("Você precisa aceitar os termos para continuar.");
            return;
        }
        setIsLoading(true);
        try {
            if (isAddingRole) {
                await addRoleToUser(role, { name, email, phone });
                navigate('/');
            } else {
                if (!email || !password || !name || !phone) {
                    setError("Por favor, preencha todos os campos.");
                    setIsLoading(false);
                    return;
                }
                await signUp(email, password, name, phone, role);
                navigate('/');
            }
        } catch (err) {
            if (err.message?.includes('auth/email-already-in-use')) {
                setError("Este e-mail já está cadastrado.");
            } else if (err.message?.includes('auth/weak-password')) {
                setError("A senha deve ter pelo menos 6 caracteres.");
            } else if (err.message?.includes('auth/invalid-email')) {
                setError("O formato do e-mail é inválido.");
            } else {
                setError("Ocorreu um erro ao realizar o cadastro.");
            }
            setIsLoading(false);
        }
    };

    const backgroundStyle = {
        background: role === 'client'
            ? `linear-gradient(to bottom, ${COLORS.PRIMARY_BLUE}, ${COLORS.LIGHT_GRAY})`
            : `linear-gradient(to bottom, ${COLORS.SUCCESS_GREEN}, ${COLORS.LIGHT_GRAY})`,
        transition: 'background 0.5s ease',
    };

    const isButtonDisabled = isLoading || !termsAccepted;

    const handleSelectRole = (selectedRole) => {
        setRole(selectedRole);
        setRoleSelected(true);
    };

    return (
        <div className="flex flex-col items-center justify-start min-h-screen p-4 pt-12 relative overflow-y-auto" style={backgroundStyle}>
            {/* Botão de voltar ajustado para o fluxo de dois passos */}
            <button
                onClick={() => roleSelected && !isAddingRole ? setRoleSelected(false) : navigate(-1)}
                className="absolute top-6 left-6 p-2 rounded-full bg-white/20 hover:bg-white/40 transition z-10"
            >
                <ArrowLeft className="w-6 h-6 text-white"/>
            </button>

            <div className="w-full max-w-md text-left z-0 mt-4">
                <div className="text-center mb-8 px-4">
                    <h1 className="text-3xl font-black text-white uppercase tracking-tighter">
                        {isAddingRole ? 'Novo Perfil' : (!roleSelected ? 'Seja bem-vindo!' : 'Quase lá...')}
                    </h1>
                    <p className="text-white opacity-90 text-sm font-medium">
                        {isAddingRole
                            ? `Você está adicionando o perfil de ${role === 'client' ? 'Cliente' : 'Prestador'}.`
                            : (!roleSelected ? 'Como você deseja utilizar o T-SERVI?' : 'Preencha seus dados de cadastro.')}
                    </p>
                </div>

                {!roleSelected && !isAddingRole ? (
                    /* PASSO 1: SELEÇÃO DE PERFIL COM CARDS GRANDES */
                    <div className="space-y-4 animate-fade-in">
                        <button
                            onClick={() => handleSelectRole('client')}
                            className="w-full p-6 bg-white rounded-[2.5rem] shadow-xl border-4 border-transparent hover:border-blue-300 transition-all flex items-center group active:scale-95"
                        >
                            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform">
                                <Search size={32} />
                            </div>
                            <div className="flex-1 text-left">
                                <h3 className="text-xl font-black text-gray-800 uppercase leading-none">Cliente</h3>
                                <p className="text-xs font-black text-blue-600 mt-1 uppercase tracking-widest">Contratante de serviço</p>
                                <p className="text-[11px] text-gray-500 font-bold mt-2 leading-tight">Para quem precisa contratar profissionais qualificados perto de você.</p>
                            </div>
                            <ChevronRight size={20} className="text-gray-300" />
                        </button>

                        <button
                            onClick={() => handleSelectRole('servi')}
                            className="w-full p-6 bg-white rounded-[2.5rem] shadow-xl border-4 border-transparent hover:border-emerald-300 transition-all flex items-center group active:scale-95"
                        >
                            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mr-5 group-hover:scale-110 transition-transform">
                                <Briefcase size={32} />
                            </div>
                            <div className="flex-1 text-left">
                                <h3 className="text-xl font-black text-gray-800 uppercase leading-none">SERVI</h3>
                                <p className="text-xs font-black text-emerald-600 mt-1 uppercase tracking-widest">Prestador de serviço</p>
                                <p className="text-[11px] text-gray-500 font-bold mt-2 leading-tight">Para quem busca visibilidade e gestão do seu negócio.</p>
                            </div>
                            <ChevronRight size={20} className="text-gray-300" />
                        </button>

                        <p className="text-center text-xs font-bold text-white mt-10 uppercase tracking-widest opacity-80">
                            Já tem uma conta? <Link to="/login" className="underline decoration-2 underline-offset-4 text-white">Acesse aqui</Link>
                        </p>
                    </div>
                ) : (
                    /* PASSO 2: FORMULÁRIO DE CADASTRO */
                    <div className="bg-white shadow-2xl rounded-[2.5rem] overflow-hidden animate-slide-up">
                        {/* Indicador de Perfil Selecionado */}
                        <div className={`p-4 flex items-center justify-between px-8 ${role === 'client' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                            <div className="flex items-center">
                                <div className={`p-2 rounded-lg mr-3 ${role === 'client' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                    {role === 'client' ? <Search size={16} /> : <Briefcase size={16} />}
                                </div>
                                <span className={`text-[10px] font-black uppercase tracking-widest ${role === 'client' ? 'text-blue-700' : 'text-emerald-700'}`}>
                                    Perfil {role === 'client' ? 'Contratante' : 'Prestador'}
                                </span>
                            </div>
                            {!isAddingRole && (
                                <button onClick={() => setRoleSelected(false)} className="text-[9px] font-black text-gray-400 uppercase flex items-center hover:text-gray-600 transition-colors">
                                    <RefreshCw size={10} className="mr-1" /> Mudar Escolha
                                </button>
                            )}
                        </div>

                        <form onSubmit={handleSignUp} className="p-8 space-y-4">
                             <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input type="text" placeholder="Nome Completo" value={name} onChange={(e) => setName(e.target.value)} disabled={isAddingRole} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 transition-all outline-none text-sm font-semibold"/>
                            </div>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isAddingRole} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 transition-all outline-none text-sm font-semibold"/>
                            </div>

                            {!isAddingRole && (
                                <>
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input type={showPassword ? "text" : "password"} placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-12 pr-12 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 transition-all outline-none text-sm font-semibold"/>
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors">
                                            {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input type={showPassword ? "text" : "password"} placeholder="Confirmar Senha" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 transition-all outline-none text-sm font-semibold"/>
                                    </div>
                                </>
                            )}

                             <div className="relative">
                                <PhoneCall className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"/>
                                <input type="tel" placeholder="Celular com DDD" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 transition-all outline-none text-sm font-semibold" />
                            </div>

                            <div className="flex items-start space-x-3 pt-2">
                                <input id="terms" name="terms" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1 cursor-pointer"/>
                                <div className="text-[11px] leading-tight font-medium text-gray-500 text-left">
                                    <label htmlFor="terms">
                                        Li e aceito os{' '}
                                        <Link to={role === 'client' ? "/termos/cliente" : "/termos/servi"} target="_blank" className="font-bold text-gray-800 underline uppercase tracking-tighter">Termos de Uso</Link> e a{' '}
                                        <Link to="/privacidade" target="_blank" className="font-bold text-gray-800 underline uppercase tracking-tighter">Privacidade</Link>.
                                    </label>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-100 p-3 rounded-xl">
                                    <p className="text-red-600 text-xs text-center font-bold uppercase tracking-tight">{error}</p>
                                </div>
                            )}

                            <button type="submit" disabled={isButtonDisabled} className="w-full py-4 text-white font-black rounded-2xl shadow-xl transition-all active:scale-95 uppercase tracking-widest text-sm disabled:bg-gray-200" style={{backgroundColor: isButtonDisabled ? '' : (role === 'client' ? COLORS.PRIMARY_BLUE : COLORS.SUCCESS_GREEN)}}>
                                {isLoading ? 'Aguarde...' : (isAddingRole ? 'Adicionar Perfil' : 'Cadastrar')}
                            </button>
                        </form>
                    </div>
                )}

                {roleSelected && (
                    <p className="text-center text-xs font-bold text-white mt-8 uppercase tracking-widest opacity-80">
                        {!isAddingRole && 'Já tem uma conta?'} <Link to="/login" className="underline decoration-2 underline-offset-4 text-white">{!isAddingRole && 'Acesse aqui'}</Link>
                    </p>
                )}
            </div>
        </div>
    );
};

export default SignUpScreen;
